/**
 * Shared utility for deriving responsive srcSet from an original image path.
 *
 * Uses a predictable filename convention: `<stem>-<width>w.webp`
 * placed alongside the original file. No manifest needed.
 */

/** Widths generated by the Vite plugin / standalone script */
const RESPONSIVE_WIDTHS = [640, 1080, 1920, 2560] as const;

/** Extensions that are eligible for responsive variants */
const RASTER_RE = /\.(jpe?g|png|webp)$/i;

export interface ResponsiveImageInfo {
  /** WebP srcSet string for use in <source> */
  srcSet: string;
  /** Original path (unchanged) for <img> fallback */
  fallbackSrc: string;
}

/**
 * Build a WebP `srcSet` string from an original image path.
 *
 * Returns `null` for non-raster sources (video, SVG, Lottie, etc.) and for
 * remote/absolute URLs (variants only exist for locally bundled assets)
 * so callers can fall back to a plain `<img>`.
 *
 * Note: srcSet always includes all four widths. When the source image is
 * narrower than a target width, the generator produces the variant at the
 * source's native width (no upscaling) so the file always exists.
 */
export function getResponsiveSrcSet(
  originalSrc: string,
): ResponsiveImageInfo | null {
  const isLocalPath =
    originalSrc.startsWith("/") && !originalSrc.startsWith("//");
  if (!isLocalPath) return null;
  if (!RASTER_RE.test(originalSrc)) return null;

  const dotIndex = originalSrc.lastIndexOf(".");
  const stem = originalSrc.slice(0, dotIndex);

  const srcSet = RESPONSIVE_WIDTHS.map((w) => `${stem}-${w}w.webp ${w}w`).join(
    ", ",
  );

  return { srcSet, fallbackSrc: originalSrc };
}
